import { createContext, useContext } from "react";

import type { DocumentWorkflowSignatureCapabilities } from "../entities/document-workflow";
import type { ShellServices } from "./services";

/** Что известно о возможностях подписи: до её действий сервер спрашивают. */
export type SignatureCapabilitiesState =
  | { readonly state: "pending" }
  | { readonly state: "error"; readonly retry: () => void }
  | {
      readonly state: "ready";
      readonly data: DocumentWorkflowSignatureCapabilities;
    };

/** Общие сведения для строк файлов, версий и подписей документа. */
export interface DocumentFilesContextValue {
  readonly services: ShellServices;
  readonly documentId: string;
  readonly accessScopeId?: string;
  readonly canUpload: boolean;
  /** Право связывать и отвязывать файлы: только подсказка интерфейсу. */
  readonly canUnlink: boolean;
  /** `can_sign` документа: только подсказка интерфейсу. */
  readonly canSign: boolean;
  readonly canViewDiscussion: boolean;
  /** Имя сотрудника по идентификатору; без имени показывается нейтральная подпись. */
  readonly authorName: (id: string | null) => string;
  /** Перейти к письму, из которого пришёл файл. */
  readonly onShowInMail?: (messageId: string) => void;
  /** Перейти к сообщению обсуждения, из которого пришёл файл. */
  readonly onShowInDiscussion?: (commentId: string) => void;
  readonly signatureCapabilities: SignatureCapabilitiesState;
  /** Элемент с общей причиной недоступности подписи: на него ссылаются кнопки. */
  readonly signingNoticeId: string;
  /**
   * Версия, только что добавленная из переписки или обсуждения ради подписи:
   * её панель подписания открывается сразу.
   */
  readonly autoSignVersionId: string | null;
  readonly requestSigning: (versionId: string) => void;
}

export const DocumentFilesContext =
  createContext<DocumentFilesContextValue | null>(null);

export function useDocumentFiles(): DocumentFilesContextValue {
  const value = useContext(DocumentFilesContext);
  if (value === null) {
    throw new Error("DocumentFilesContext отсутствует.");
  }

  return value;
}
